/**
 * /hotels — Hotel search entrypoint.
 *
 * Destination autocomplete + date range picker + rooms/adults/children
 * steppers. Submits to `/hotels/search?...` with URL-encoded params.
 *
 * Uses native <input type="date"> for date pickers to avoid pulling
 * in another React date-picker dep — every modern browser has a solid
 * built-in picker with min/max validation.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BedDouble, MapPin, Users, Minus, Plus, Search, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function OccupancyStepper({ label, value, onChange, min = 0, max = 8, testId }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="font-hand text-tb-navy text-sm">{label}</span>
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testId}-dec`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 rounded-full border-2 border-tb-navy/30 text-tb-navy disabled:opacity-40 flex items-center justify-center tb-tap hover:border-tb-navy"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span data-testid={`${testId}-value`} className="min-w-[1.5rem] text-center font-hand text-tb-navy text-base">
          {value}
        </span>
        <button
          type="button"
          data-testid={`${testId}-inc`}
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          className="w-8 h-8 rounded-full border-2 border-tb-navy/30 text-tb-navy disabled:opacity-40 flex items-center justify-center tb-tap hover:border-tb-navy"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function HotelsSearchPage() {
  const nav = useNavigate();
  const [dest, setDest] = useState(null); // {code, name, country}
  const [destQuery, setDestQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [checkIn, setCheckIn] = useState(todayPlus(30));
  const [checkOut, setCheckOut] = useState(todayPlus(34));
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [childAges, setChildAges] = useState([]);
  const [err, setErr] = useState(null);
  const debounced = useDebounced(destQuery, 300);
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!debounced || debounced.length < 1) {
      // Show trending starter list even before user types
      api.get("/hotels/destinations/search?q=&limit=8")
        .then(({ data }) => { if (alive) setSuggestions(data || []); })
        .catch(() => { if (alive) setSuggestions([]); });
      return () => { alive = false; };
    }
    api.get(`/hotels/destinations/search?q=${encodeURIComponent(debounced)}&limit=10`)
      .then(({ data }) => { if (alive) setSuggestions(data || []); })
      .catch(() => { if (alive) setSuggestions([]); });
    return () => { alive = false; };
  }, [debounced]);

  // Sync childAges array length to `children` count.
  useEffect(() => {
    setChildAges((prev) => {
      const next = [...prev];
      while (next.length < children) next.push(8);
      next.length = children;
      return next;
    });
  }, [children]);

  const canSubmit = useMemo(() => {
    if (!dest) return false;
    if (!checkIn || !checkOut) return false;
    if (checkOut <= checkIn) return false;
    if (adults < 1) return false;
    if (children > 0 && childAges.some((a) => a === null || a === undefined || a === "")) return false;
    return true;
  }, [dest, checkIn, checkOut, adults, children, childAges]);

  const onSubmit = () => {
    if (!canSubmit) {
      setErr("Pick a destination and valid dates first.");
      return;
    }
    setErr(null);
    const occ = {
      rooms: 1,
      adults,
      children,
      paxes: [
        ...Array.from({ length: adults }, () => ({ type: "AD", age: 30 })),
        ...childAges.map((a) => ({ type: "CH", age: Number(a) })),
      ],
    };
    const params = new URLSearchParams({
      destinationCode: dest.code,
      destinationName: dest.name,
      checkIn,
      checkOut,
      occupancies: JSON.stringify([occ]),
    });
    nav(`/hotels/search?${params.toString()}`);
  };

  return (
    <div data-testid="hotels-search-page" className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-5 md:px-8 pt-6 pb-16">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/20 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        <div className="mt-6 flex items-start justify-between gap-4">
          <div>
            <h1
              data-testid="hotels-search-title"
              className="text-tb-navy text-4xl md:text-5xl leading-tight font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Book your stay
            </h1>
            <p className="mt-2 font-hand text-tb-navy/60 text-base">
              Real-time availability from Hotelbeds. Confirmed instantly.
            </p>
          </div>
          <PoweredByHotelbeds />
        </div>

        <div className="mt-8 rounded-2xl border-2 border-tb-cream-3 bg-white p-5 md:p-6 shadow-sm">
          {/* Destination */}
          <div className="relative" ref={boxRef}>
            <label className="font-hand text-tb-navy/70 text-xs uppercase tracking-widest">
              Where to
            </label>
            <div className="mt-1 flex items-center gap-2 border-b-2 border-tb-navy/20 focus-within:border-tb-navy py-2">
              <MapPin className="w-4 h-4 text-tb-navy/60 shrink-0" />
              <input
                data-testid="hotels-search-destination"
                type="text"
                placeholder="City, region, or country"
                value={dest ? `${dest.name}, ${dest.country}` : destQuery}
                onChange={(e) => {
                  setDest(null);
                  setDestQuery(e.target.value);
                  setSuggestOpen(true);
                }}
                onFocus={() => setSuggestOpen(true)}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 200)}
                className="flex-1 bg-transparent outline-none font-hand text-tb-navy text-lg placeholder:text-tb-navy/35"
              />
            </div>
            {suggestOpen && suggestions.length > 0 && (
              <ul
                data-testid="hotels-search-suggestions"
                className="absolute z-10 mt-1 left-0 right-0 max-h-72 overflow-y-auto bg-white border-2 border-tb-cream-3 rounded-xl shadow-lg"
              >
                {suggestions.map((s) => (
                  <li key={s.code}>
                    <button
                      type="button"
                      data-testid={`hotels-search-suggestion-${s.code}`}
                      onClick={() => {
                        setDest(s);
                        setDestQuery("");
                        setSuggestOpen(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-tb-navy/5 flex items-center gap-2 font-hand text-tb-navy tb-tap"
                    >
                      <MapPin className="w-3.5 h-3.5 text-tb-navy/60" />
                      <span className="font-medium">{s.name}</span>
                      <span className="text-tb-navy/50 text-sm">{s.country}</span>
                      <span className="ml-auto text-tb-navy/40 text-[11px] uppercase tracking-widest">{s.code}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Dates */}
          <div className="mt-5 grid grid-cols-2 gap-4">
            <div>
              <label className="font-hand text-tb-navy/70 text-xs uppercase tracking-widest">Check-in</label>
              <input
                data-testid="hotels-search-checkin"
                type="date"
                value={checkIn}
                min={todayPlus(1)}
                max={todayPlus(720)}
                onChange={(e) => setCheckIn(e.target.value)}
                className="mt-1 w-full border-b-2 border-tb-navy/20 focus:border-tb-navy py-2 bg-transparent font-hand text-tb-navy text-lg outline-none"
              />
            </div>
            <div>
              <label className="font-hand text-tb-navy/70 text-xs uppercase tracking-widest">Check-out</label>
              <input
                data-testid="hotels-search-checkout"
                type="date"
                value={checkOut}
                min={checkIn || todayPlus(2)}
                max={todayPlus(725)}
                onChange={(e) => setCheckOut(e.target.value)}
                className="mt-1 w-full border-b-2 border-tb-navy/20 focus:border-tb-navy py-2 bg-transparent font-hand text-tb-navy text-lg outline-none"
              />
            </div>
          </div>

          {/* Guests */}
          <div className="mt-5">
            <div className="flex items-center gap-2 text-tb-navy/70">
              <Users className="w-4 h-4" />
              <span className="font-hand text-xs uppercase tracking-widest">Guests</span>
            </div>
            <OccupancyStepper testId="hotels-search-adults" label="Adults (18+)" value={adults} onChange={setAdults} min={1} max={8} />
            <OccupancyStepper testId="hotels-search-children" label="Children (0-17)" value={children} onChange={setChildren} min={0} max={8} />
            {children > 0 && (
              <div data-testid="hotels-search-child-ages" className="mt-1 grid grid-cols-4 gap-2">
                {childAges.map((age, i) => (
                  <div key={i} className="flex flex-col">
                    <label className="font-hand text-tb-navy/55 text-[10px] uppercase tracking-widest">Child {i + 1} age</label>
                    <input
                      data-testid={`hotels-search-child-age-${i}`}
                      type="number"
                      value={age ?? ""}
                      min={0}
                      max={17}
                      required
                      onChange={(e) => {
                        const nx = [...childAges];
                        nx[i] = e.target.value === "" ? "" : Math.max(0, Math.min(17, Number(e.target.value)));
                        setChildAges(nx);
                      }}
                      className="mt-1 border-b-2 border-tb-navy/20 focus:border-tb-navy py-1 bg-transparent font-hand text-tb-navy text-base outline-none"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {err && (
            <div data-testid="hotels-search-error" className="mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 font-hand text-red-700 text-sm">
              {formatApiErrorDetail(err)}
            </div>
          )}

          <button
            data-testid="hotels-search-submit"
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-45 hover:brightness-110"
          >
            <Search className="w-5 h-5" />
            Search hotels
          </button>
        </div>

        <p className="mt-6 text-center font-hand text-tb-navy/45 text-xs italic">
          Prices shown in the hotel&apos;s currency. Taxes and fees may
          be included or payable at the property — always shown before
          you confirm.
        </p>
      </div>
    </div>
  );
}
